import React from "react";
import MalattiaRaraTemplate from "../../components/malattia-rara/MalattiaRaraTemplate";
import image1 from "../../assets/ipercolesterolemia_familiare.jpg";

const IpercolesterolemiaFamiliarePage: React.FC = () => (
  <MalattiaRaraTemplate
    titolo="Ipercolesterolemia Familiare"
    descrizione={[
      "L’ipercolesterolemia familiare (FH) è una patologia genetica del metabolismo lipidico a trasmissione autosomica dominante. La forma omozigote (HoFH) è estremamente rara, mentre la forma eterozigote (HeFH) colpisce, solo in Italia, un numero di individui che, secondo le stime più recenti, potrebbe raggiungere le 250.000-300.000 unità.",
      "I pazienti affetti da questa condizione presentano sin dalla nascita elevati livelli di colesterolo LDL (C-LDL), con un conseguente sviluppo precoce di aterosclerosi coronarica. Questo aumento del rischio cardiovascolare porta spesso a eventi clinici gravi in età adulta, tra cui infarto miocardico, angina e morte improvvisa. Tuttavia, la FH rimane sotto-diagnosticata e sotto-trattata, nonostante la disponibilità di terapie efficaci in grado di ridurre il rischio di eventi cardiovascolari precoci. La malattia è causata da mutazioni nei geni coinvolti nel metabolismo del recettore delle LDL e nel suo processo di riciclo (LDL-R recycling), determinando una riduzione dell’assorbimento cellulare delle LDL e un conseguente aumento delle concentrazioni plasmatiche di C-LDL.",
      "L'impatto sui livelli di colesterolo e sul rischio cardiovascolare varia in base allo stato genetico del paziente:",
      " - Gli uomini e le donne con HeFH presentano livelli plasmatici di colesterolo compresi tra 300 e 600 mg/dl (8-15 mmol/l) e tendono a sviluppare eventi cardiovascolari prima dei 55 anni negli uomini e dei 60 anni nelle donne.",
      " - Gli omozigoti (HoFH) hanno livelli di colesterolo superiori a 500 mg/dl (>13 mmol/l) e manifestano complicanze cardiovascolari già in giovane età, con un'elevata mortalità entro i 20-25 anni.",
      "Negli ultimi anni, nuove strategie terapeutiche hanno iniziato a modificare il decorso della malattia. Farmaci come evinacumab e lomitapide, che agiscono su specifici pathway molecolari alterati, stanno rivoluzionando il trattamento dell'ipercolesterolemia familiare, migliorando il controllo del C-LDL e l'outcome clinico dei pazienti. Il progresso nelle tecniche diagnostiche e l’impiego di terapie mirate rappresentano strumenti fondamentali per migliorare la prognosi di questa popolazione ad alto rischio cardiovascolare.",
    ]}
    immagineDescrizione={image1}
    altImmagineDescrizione="Depositi di colesterolo (xantomi) caratteristici dell'ipercolesterolemia familiare"
    sintomi={[
      "Livelli elevati di colesterolo LDL (>190 mg/dL negli adulti con forma eterozigote, >500 mg/dL nella forma omozigote)",
      "Xantomi tendinei (depositi di colesterolo nei tendini, in particolare tendine di Achille ed estensori delle dita)",
      "Xantelasmi (depositi di colesterolo sulle palpebre)",
      "Arco corneale prematuro (anello biancastro intorno all'iride prima dei 45 anni)",
      "Malattia coronarica precoce (infarto miocardico prima dei 55 anni negli uomini e 65 nelle donne)",
      "Angina pectoris",
      "Stenosi aortica (nella forma omozigote)",
      "Claudicatio intermittens (dolore agli arti inferiori durante la deambulazione)",
      "Ictus ischemico precoce",
      "Storia familiare di ipercolesterolemia e/o eventi cardiovascolari prematuri",
    ]}
    diagnosi={[
      "Profilo lipidico completo a digiuno: colesterolo totale, LDL, HDL, trigliceridi (elevati valori di LDL sono caratteristici)",
      "Esame obiettivo mirato alla ricerca di xantomi tendinei, xantelasmi, arco corneale",
      "Anamnesi familiare dettagliata per ipercolesterolemia e malattie cardiovascolari premature",
      "Applicazione di criteri diagnostici standardizzati: criteri del Dutch Lipid Clinic Network, criteri MEDPED, criteri Simon Broome",
      "Test genetico: identificazione di mutazioni nei geni LDLR, APOB, PCSK9, LDLRAP1",
      "Valutazione del rischio cardiovascolare globale",
      "Ecografia dei tendini (in particolare tendine di Achille) per rilevare xantomi subclinici",
      "Valutazione dell'aterosclerosi subclinica: ecografia carotidea per misurare lo spessore medio-intimale, TC coronarica per il calcium score",
      "Screening a cascata dei familiari di primo grado",
    ]}
    trattamento={[
      "Modifiche dello stile di vita: dieta povera di grassi saturi e colesterolo, esercizio fisico regolare, astensione dal fumo",
      "Terapia farmacologica intensiva:",
      "- Statine ad alta intensità (prima linea): atorvastatina, rosuvastatina alle massime dosi tollerate",
      "- Ezetimibe: in associazione alle statine",
      "- Inibitori di PCSK9 (evolocumab, alirocumab): per pazienti che non raggiungono i target con statine ed ezetimibe",
      "- Acido bempedoico: nuova opzione per pazienti intolleranti alle statine",
      "- Sequestranti degli acidi biliari (colestiramina, colestipolo): opzioni aggiuntive",
      "Terapie speciali per la forma omozigote:",
      "- LDL-aferesi: rimozione extracorporea delle particelle LDL ogni 1-2 settimane",
      "- Lomitapide (inibitore della proteina di trasferimento dei trigliceridi microsomiali)",
      "- Evinacumab (anticorpo monoclonale contro ANGPTL3)",
      "Trattamento delle complicanze cardiovascolari: rivascolarizzazione coronarica, sostituzione valvolare aortica",
      "Trapianto di fegato in casi selezionati di ipercolesterolemia familiare omozigote",
      "Screening periodico per aterosclerosi subclinica",
      "Counseling genetico e screening familiare",
    ]}
    immagineTrattamento="https://via.placeholder.com/400x300"
    altImmagineTrattamento="Trattamento dell'ipercolesterolemia familiare con LDL-aferesi"
    epidemiologia={{
      prevalenza:
        "Forma eterozigote: 1:250 nella popolazione generale; Forma omozigote: 1:300.000-1:1.000.000",
      incidenza: "Non nota con precisione",
      etaMedia:
        "Diagnosi spesso tardiva (30-40 anni) nelle forme eterozigoti; diagnosi in età pediatrica nelle forme omozigoti",
      rapportoSessi:
        "M:F = 1:1 (distribuzione genetica), ma maggiore morbilità cardiovascolare nei maschi",
    }}
    riferimenti={[
      {
        nome: "Nordestgaard BG, et al. Familial hypercholesterolaemia is underdiagnosed and undertreated in the general population: guidance for clinicians to prevent coronary heart disease. European Heart Journal. 2013",
        link: "https://www.esempio.it/riferimento1",
      },
      {
        nome: "Mach F, et al. 2019 ESC/EAS Guidelines for the management of dyslipidaemias: lipid modification to reduce cardiovascular risk. European Heart Journal. 2020",
        link: "https://www.esempio.it/riferimento2",
      },
      {
        nome: "FH Foundation - Organizzazione dedicata alla sensibilizzazione e supporto per l'ipercolesterolemia familiare",
        link: "https://www.esempio.it/riferimento3",
      },
      {
        nome: "Orphanet - Informazioni sull'Ipercolesterolemia Familiare",
        link: "https://www.esempio.it/riferimento4",
      },
    ]}
  />
);

export default IpercolesterolemiaFamiliarePage;
